/*
 * Copyright (c) 2026. Bluemate B.V.
 */

import React, {ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';

import {generateComponentId} from '@dvrd/dvr-controls';

import CodeInput from './codeInput';

interface Props {
    onSubmit: (code: string) => void;
    // Default 4
    length?: number;
    disabled?: boolean;
    className?: string;
    numbersOnly?: boolean;
    id?: string;
    autoSubmit?: boolean;
    uppercase?: boolean;
}

export type CodeInputRef = {submit: VoidFunction};

function CodeInputController(props: Props, ref: ForwardedRef<CodeInputRef>) {
    const {onSubmit, disabled, className, numbersOnly, autoSubmit, uppercase} = props;
    const id = useRef(generateComponentId(props.id));
    const length = props.length ?? 4;
    const [codeParts, setCodeParts] = useState(Array(length ?? 4).fill(''));
    const fullCode = useMemo(() => codeParts.join(''), [codeParts]);

    function onChange(index: number) {
        return function (evt: React.ChangeEvent<HTMLInputElement>) {
            // Prevent default input change to prevent glitchy text changes during pasting
            evt.preventDefault();
            let value = evt.target.value;
            if (numbersOnly && value.match(/\D/g)) {
                value = value.replace(/\D/g, '').trim();
                if (!value.length || value === codeParts[index]) return;
            }
            if (uppercase) value = value.toUpperCase();
            const newCodeParts = codeParts.slice();
            if (!value.length) {
                newCodeParts[index] = '';
                setCodeParts(newCodeParts);
                return;
            }
            const valueParts = value.split('');
            let partIndex = index;
            if (valueParts.length > 1)
                // Pasting, start from the first field
                partIndex = 0;
            for (const valuePart of valueParts) {
                newCodeParts[partIndex] = valuePart;
                if (++partIndex >= length) break;
            }
            setCodeParts(newCodeParts);

            // If typing, focus next input
            if (value.length === 1) onFocusNext(index + 1);
            else {
                const focusIndex = Math.min(value.length, index + 1 + valueParts.length, length);
                onFocusNext(focusIndex - 1);
            }
        };
    }

    function onEnter(evt: React.KeyboardEvent) {
        if (evt.key === 'Enter') onSubmit(fullCode);
    }

    function submit() {
        onSubmit(fullCode);
    }

    function onFocusNext(index: number) {
        if (index >= length) return; // Already at last input
        document.getElementById(`${id.current}-${index}`)?.focus();
    }

    useImperativeHandle(ref, () => ({submit}));

    useEffect(() => {
        if (autoSubmit && fullCode.length === length) onSubmit(fullCode);
    }, [autoSubmit, fullCode, length]);

    return (
        <CodeInput
            onChange={onChange}
            onEnter={onEnter}
            codeParts={codeParts}
            length={length}
            id={id.current}
            className={className}
            disabled={disabled}
        />
    );
}

export default forwardRef<CodeInputRef, Props>(CodeInputController);
